import React, {useCallback, useEffect, useState, useRef} from 'react';
import {Box, CircularProgress, Typography, TextField, Button} from '@mui/material';
import ZoomControls from '../ZoomControls/ZoomControls';
import PdfScrollContainer from '../PdfScrollContainer/PdfScrollContainer';
import PageIndicator from '../PageIndicator/PageIndicator';
import ResizablePanel from '../hooks/ResizablePanel/ResizablePanel';
import useZoom from '../hooks/useZoom/useZoom';
import usePdfExtraction from '../hooks/usePdfExtraction';
import usePageTracking from '../hooks/usePageTracking';
import {pdfjs} from 'react-pdf';
import CreateIcon from '@mui/icons-material/Create';
import {useSelectAnnotationBox} from '../hooks/useSelectAnnotationBox';
import GroupWorkIcon from '@mui/icons-material/GroupWork';
import Draggable from 'react-draggable';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import {useTheme, alpha} from '@mui/material/styles';
import {useTranslation} from "react-i18next";


pdfjs.GlobalWorkerOptions.workerSrc =
    `${process.env.PUBLIC_URL || ''}/pdf.worker.mjs`;


// HighlightOverlay.js
const getAnnotationKey = (a) => {
    if (a.__uid) return a.__uid;
    const base = `${a.page}_${a.type}`;

    if (a.type === 'key_value') {
        const v = a.value?.bbox?.join('_') || 'no_val_bbox';
        return `${base}_${v}`;
    }

    if (a.type === 'entity' || a.type === 'summary_row') return `${base}_${a.bbox?.join('_')}`;
    if (a.type === 'table_row' && Array.isArray(a.columns)) {
        const coords = a.columns.map(col => col.bbox?.join('_')).join('|');
        return `${base}_${coords}`;
    }
    if (a.type === 'table' && Array.isArray(a.bbox)) return `${base}_${a.bbox.join('_')}`;
    return `${base}_fallback_${Math.random().toString(36).slice(2, 8)}`;
};

const X_OFFSET_PDF = 10;
const Y_OFFSET_PDF = 10;

const getBackendBboxFromDisplayBbox = (bbox) => {
    if (!Array.isArray(bbox) || bbox.length !== 4) return bbox;
    return [
        bbox[0] - X_OFFSET_PDF,
        bbox[1] - Y_OFFSET_PDF,
        bbox[2] - X_OFFSET_PDF,
        bbox[3] - Y_OFFSET_PDF,
    ];
};


// Find the nearest scrollable ancestor of an element
function getScrollParent(el) {
    let node = el?.parentElement;
    while (node) {
        const style = window.getComputedStyle(node);
        const overflowY = style.overflowY;
        const canScrollY =
            (overflowY === 'auto' || overflowY === 'scroll') &&
            node.scrollHeight > node.clientHeight;
        if (canScrollY) return node;
        node = node.parentElement;
    }
    // fallback to document scroller
    return document.scrollingElement || document.documentElement;
}


function PdfViewer({
                       file,
                       provider,
                       model,
                       batchSize,
                       maxWorkers,
                       useOcr,
                       useDeepScan,
                       onExportData,
                       annotationOrder,
                       setAnnotationOrder,
                       groupAnnotationOrder,
                       setGroupAnnotationOrder,
                       groupMeta,
                       setGroupMeta,
                       selectedAnnotations,
                       setSelectedAnnotations,
                       initialHighlights,
                       disableExtraction,
                       defaultGroupIdForNewAnns,
                       hideSelectionPanel = false,   // hide right panel
                       disableInteractions = false,  // make rects non-clickable / no drawing
                       forceAllSelected = false,      // visually select all rects
                       hideToolbar = false,
                       showZoomControls = true,
                       showAnnotateControls = true,
                       hideEmptyStateCta = false,
                       externalPeekKey,
                       externalPeekNonce,
                       externalHoveredKey,
                       onBoxHover,
                       externalRemoveKey,
                       externalRemoveNonce,
                       onAnnotationAdded,
                       onAnnotationMoved,
                       onAnnotationsChange,
                       persistSelection = false,
                       externalFocusPage,
                       tableWarningText,
                       disableReprocessOnDrag = false, // if true, don't reprocess when dragging boxes
                   }) {
    const {t} = useTranslation();

    const theme = useTheme();

    const {scale, handleZoomIn, handleZoomOut} = useZoom();

    const {
        highlights: extractedHighlights,
        loading,
        executionTime,
        selectedAnnotation,
        setSelectedAnnotation,
    } = usePdfExtraction({
        file,
        provider,
        model,
        batchSize,
        maxWorkers,
        useOcr,
        useDeepScan,
        disabled: disableExtraction,
    });

    const highlights = disableExtraction ? initialHighlights : extractedHighlights;

    const {viewerRef, pageRefs, numPages, setNumPages, currentPage} = usePageTracking();

    const [annotations, setAnnotations] = useState([]);

    const [selectedKeys, setSelectedKeys] = useState(() => {
        // Initialize state directly from props to prevent race conditions on mount.
        return (selectedAnnotations || []).map(getAnnotationKey);
    });
    const [isDrawing, setIsDrawing] = useState(false);

    const [multiSelectMode, setMultiSelectMode] = useState(false);

    const [groupName, setGroupName] = useState('');

    const [multiSelectTempKeys, setMultiSelectTempKeys] = useState([]);

    const dragRef = useRef(null);

    const [isDragging, setIsDragging] = useState(false);

    const groupNameInputRef = useRef(null);

    const [peekKey, setPeekKey] = useState(null); // which annotation to wiggle
    const [hoveredKey, setHoveredKey] = useState(null); // which annotation is being hovered

    // when true: drawing a box won't call the snippet backend
    const [redrawOnly, setRedrawOnly] = useState(false);

    const removedKeysRef = useRef(new Set());

    const peekTimersRef = useRef({set: null, clear: null});
    const lastExternalRemoveNonceRef = useRef(externalRemoveNonce);

    const finishDrawing = useCallback(() => {
        setIsDrawing(false);
        setRedrawOnly(false);
    }, []);

    // Clear annotations when OCR mode changes to prevent mixing digital and scanned results
    const prevUseOcrRef = useRef(useOcr);
    useEffect(() => {
        if (prevUseOcrRef.current !== useOcr) {
            setAnnotations([]);
            removedKeysRef.current = new Set();
            prevUseOcrRef.current = useOcr;
        }
    }, [useOcr]);

    // Bring the page into view and resolve only after it's visible *and* its scroller is idle.
    const ensureKeyVisible = (
        rawKey,
        {retryDelay = 200, max = 3, idleMs = 200, threshold = 0.3} = {}
    ) => {
        return new Promise((resolve) => {
            try {
                if (!rawKey || !pageRefs?.current) return resolve();
                const key = Array.isArray(rawKey) ? rawKey[0] : rawKey;
                const keyOf = (a) => a.__uid || getAnnotationKey(a);
                const ann = (annotations || []).find((a) => keyOf(a) === key);
                if (!ann || !Number.isFinite(ann.page)) {
                    if (max > 0) {
                        window.setTimeout(() => {
                            ensureKeyVisible(key, {retryDelay, max: max - 1, idleMs, threshold}).then(resolve);
                        }, retryDelay);
                    } else {
                        resolve();
                    }
                    return;
                }
                const pageIndex = Math.max(0, ann.page - 1);
                const pageNodeRef = pageRefs.current?.[pageIndex];
                const pageEl = pageNodeRef?.current || pageNodeRef;
                if (!pageEl) return resolve();

                // ✅ Use the *real* scroller that will be moved by scrollIntoView
                const scroller = getScrollParent(pageEl);
                let inView = false;
                let idleTimer = null;
                let resolved = false;

                const cleanup = (io) => {
                    if (resolved) return;
                    resolved = true;
                    io && io.disconnect();
                    scroller && scroller.removeEventListener('scroll', onScroll, {passive: true});
                    if ('onscrollend' in scroller) {
                        scroller.removeEventListener('scrollend', onScrollEnd);
                    }
                    window.clearTimeout(idleTimer);
                    resolve();
                };
                const onScroll = () => {
                    // reset idle timer on each scroll
                    window.clearTimeout(idleTimer);
                    idleTimer = window.setTimeout(() => {
                        if (inView) cleanup(observer);
                    }, idleMs);
                };

                const onScrollEnd = () => {
                    // If the browser supports scrollend, use it to end sooner—but only when in view.
                    if (inView) cleanup(observer);
                };

                const observer = new IntersectionObserver(
                    (entries) => {
                        for (const entry of entries) {
                            if (entry.target !== pageEl) continue;
                            inView = entry.isIntersecting && entry.intersectionRatio >= threshold;
                            // If already in view and we've been idle long enough, resolve soon.
                        }
                    },
                    {root: scroller, threshold: [0, threshold, 1]}
                );

                scroller && scroller.addEventListener('scroll', onScroll, {passive: true});
                if ('onscrollend' in scroller) {
                    scroller.addEventListener('scrollend', onScrollEnd, {once: true});
                }
                observer.observe(pageEl);

                // --- Scroll precisely to the annotation's bbox on this page ---
                // Prefer the canvas (top-left ≈ (0,0) for PDF page content)
                const canvas = pageEl.querySelector('canvas.react-pdf__Page__canvas');
                // Helper to pick the primary bbox regardless of type
                const pickBBox = (a) => {
                    if (Array.isArray(a?.bbox)) return a.bbox;
                    if (a?.value?.bbox) return a.value.bbox;
                    if (a?.field?.bbox) return a.field.bbox;
                    // tables / composite: best-effort top-left
                    if (Array.isArray(a?.columns) && a.columns[0]?.bbox) return a.columns[0].bbox;
                    return [0, 0, 0, 0];
                };
                const [x0, y0, x1, y1] = pickBBox(ann);
                const pad = 24; // keep some breathing room around the box

                // Compute the canvas's top inside the scroller's coordinate space
                const scrollerRect = scroller.getBoundingClientRect();
                const anchorRect = (canvas || pageEl).getBoundingClientRect();
                const anchorTopInScroller = scroller.scrollTop + (anchorRect.top - scrollerRect.top);
                const anchorLeftInScroller = scroller.scrollLeft + (anchorRect.left - scrollerRect.left);

                // Translate bbox (PDF units) → pixels using current scale
                const targetTop = anchorTopInScroller + y0 * scale;
                const targetBottom = anchorTopInScroller + y1 * scale;
                const targetLeft = anchorLeftInScroller + x0 * scale;
                const targetRight = anchorLeftInScroller + x1 * scale;

                // Compute desired scroll positions to ensure the bbox is fully visible
                let nextTop = scroller.scrollTop;
                const viewTop = scroller.scrollTop;
                const viewBottom = scroller.scrollTop + scroller.clientHeight;
                if (targetTop - pad < viewTop) {
                    nextTop = Math.max(0, targetTop - pad);
                } else if (targetBottom + pad > viewBottom) {
                    nextTop = Math.min(
                        scroller.scrollHeight - scroller.clientHeight,
                        targetBottom + pad - scroller.clientHeight
                    );
                }
                let nextLeft = scroller.scrollLeft;
                const viewLeft = scroller.scrollLeft;
                const viewRight = scroller.scrollLeft + scroller.clientWidth;
                if (targetLeft - pad < viewLeft) {
                    nextLeft = Math.max(0, targetLeft - pad);
                } else if (targetRight + pad > viewRight) {
                    nextLeft = Math.min(
                        scroller.scrollWidth - scroller.clientWidth,
                        targetRight + pad - scroller.clientWidth
                    );
                }

                // Smooth scroll the real scroller (not the page) so we don't recenter the page
                scroller.scrollTo({top: nextTop, left: nextLeft, behavior: 'smooth'});
                onScroll(); // start idle wait even if no scroll is needed

                // Hard fallback: don't hang longer than ~2s.
                setTimeout(() => cleanup(observer), 2000);
            } catch (e) {
                console.warn('[peek] ensureKeyVisible failed', e);
                resolve();
            }
        });
    };

    // Tune these to taste:
    const JIGGLE_DELAY_MS = 300;     // wait this long after scrolling before jiggling
    const JIGGLE_DURATION_MS = 450;  // how long the jiggle stays active (matches CSS ~380ms)

    const requestPeek = (rawKey) => {
        const key = Array.isArray(rawKey) ? rawKey[0] : rawKey;
        ensureKeyVisible(key).then(() => {
            // Clear any previous timers
            window.clearTimeout(peekTimersRef.current.set);
            window.clearTimeout(peekTimersRef.current.clear);
            // Delay before starting the jiggle
            peekTimersRef.current.set = window.setTimeout(() => {
                setPeekKey(key);
                // Clear jiggle after its duration
                peekTimersRef.current.clear = window.setTimeout(() => setPeekKey(null), JIGGLE_DURATION_MS);
            }, JIGGLE_DELAY_MS);
        });
    }


    useEffect(() => {
        if (!externalPeekKey) return;
        let cancelled = false;
        let tSet, tClear;
        const key = Array.isArray(externalPeekKey) ? externalPeekKey[0] : externalPeekKey;
        ensureKeyVisible(key).then(() => {
            if (cancelled) return;
            tSet = setTimeout(() => {
                if (cancelled) return;
                setPeekKey(key);
                tClear = setTimeout(() => setPeekKey(null), JIGGLE_DURATION_MS);
            }, JIGGLE_DELAY_MS);
        });
        return () => {
            cancelled = true;
            if (tSet) clearTimeout(tSet);
            if (tClear) clearTimeout(tClear);
        };
        // 🔁 fires every time nonce changes, even if key is identical
    }, [externalPeekNonce, externalPeekKey, annotations]);

    useEffect(() => {
        setHoveredKey(externalHoveredKey || null);
    }, [externalHoveredKey]);

    useEffect(() => {
        if (!externalRemoveNonce || !externalRemoveKey) return;
        if (lastExternalRemoveNonceRef.current === externalRemoveNonce) return;
        lastExternalRemoveNonceRef.current = externalRemoveNonce;
        try {
            const FORCE_DRAW = '__force_draw__';
            const keys = new Set(
                Array.isArray(externalRemoveKey) ? externalRemoveKey : [externalRemoveKey]
            );
            // Don’t treat the sentinel as a real key
            keys.delete(FORCE_DRAW);

            // remember removed keys so the normalizer won't reintroduce them
            if (keys.size) {
                removedKeysRef.current = new Set([
                    ...removedKeysRef.current,
                    ...keys,
                ]);
            }

            setAnnotations(prev =>
                prev.filter(a => {
                    const k = a.__uid || getAnnotationKey(a);
                    return !keys.has(k);
                })
            );
            setIsDrawing(true);
            setRedrawOnly(true);
        } catch (e) {
            console.warn('[externalRemove] failed', e);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [externalRemoveNonce]);


// 🔎 When a page is requested, scroll it into view and focus the viewer
    useEffect(() => {
        if (!externalFocusPage || !pageRefs?.current) return;
        try {
            const pageIndex = Math.max(0, externalFocusPage - 1);
            const pageNodeRef = pageRefs.current?.[pageIndex];
            const pageEl = pageNodeRef?.current || pageNodeRef;
            if (!pageEl) return;
            const scroller = getScrollParent(pageEl);
            pageEl.scrollIntoView({block: 'center', inline: 'nearest', behavior: 'smooth'});
            // Put keyboard focus on the scroll container for good measure
            const el = viewerRef?.current;
            if (el && typeof el.focus === 'function') {
                el.setAttribute('tabindex', '-1');
                el.focus({preventScroll: true});
            }
        } catch (e) {
            console.warn('[focusPage] failed', e);
        }
    }, [externalFocusPage, pageRefs]);

    const noop = () => {
    };

// Normalize & order highlights, compute groups, and export stable orders
    useEffect(() => {
        // 1) Start from seed highlights, filtered to exclude any removed keys
        const seed = (Array.isArray(highlights) ? highlights : []).filter((a) => {
            if (!a || typeof a !== 'object') return false;
            const k = a.__uid || getAnnotationKey(a);
            return !removedKeysRef.current.has(k);
        });

        // 2) Merge seed + current live annotations, then de-dupe by key
        const byKey = new Map();
        const push = (a) => {
            if (!a || typeof a !== 'object') return;
            const k = a.__uid || getAnnotationKey(a);
            if (!k) return;
            // seed goes first; live can replace if same key (keeps latest props)
            if (!byKey.has(k)) byKey.set(k, a);
            else byKey.set(k, {...byKey.get(k), ...a});
        };
        seed.forEach(push);
        (annotations || []).forEach(push); // <- preserve live user edits/draws

        const merged = Array.from(byKey.values());

        // Helpers
        const toNumPage = (p) => (Number.isFinite(Number(p)) ? Number(p) : null);
        const getTopY = (a) => {
            if (Array.isArray(a?.bbox)) return a.bbox[1];
            if (a?.field?.bbox) return a.field.bbox[1];
            if (a?.value?.bbox) return a.value.bbox[1];
            if (Array.isArray(a?.columns) && a.columns[0]?.bbox) return a.columns[0].bbox[1];
            return Infinity;
        };

        // Annotate with stable keys and pages
        const withKeys = merged
            .filter((a) => a && typeof a === 'object')
            .map((a) => ({
                ...a,
                page: toNumPage(a.page),
                __uid: a.__uid || getAnnotationKey(a),
            }))
            .filter((a) => a.page !== null);

        // Sort
        const sorted = withKeys
            .slice()
            .sort((a, b) => (a.page !== b.page ? a.page - b.page : getTopY(a) - getTopY(b)));

        // 3) Groups & orders (same as before)
        setGroupMeta((prev) => {
            const next = {...prev};
            for (const a of sorted) {
                if (!a?.groupId) continue;
                const label = a.group_label;
                if (label && (!next[a.groupId] || next[a.groupId].name !== label)) {
                    next[a.groupId] = {name: label};
                }
            }
            return next;
        });

        const groupMap = {};
        const ungrouped = [];
        for (const a of sorted) {
            const key = a.__uid;
            if (a.groupId) {
                if (!groupMap[a.groupId]) groupMap[a.groupId] = [];
                groupMap[a.groupId].push(key);
            } else {
                ungrouped.push({id: key, page: a.page, topY: getTopY(a)});
            }
        }

        const groupEntries = Object.entries(groupMap).flatMap(([groupId, keys]) => {
            const firstAnn = sorted.find((a) => keys.includes(a.__uid));
            if (!firstAnn) return [];
            return [{id: `group_${groupId}`, page: firstAnn.page ?? 0, topY: getTopY(firstAnn)}];
        });

        const allEntries = [...groupEntries, ...ungrouped]
            .filter((e) => e && Number.isFinite(e.page) && Number.isFinite(e.topY));

        const finalOrder = allEntries
            .sort((a, b) => (a.page !== b.page ? a.page - b.page : a.topY - b.topY))
            .map((e) => e.id);

        // 4) Commit only if different
        setAnnotations((prevAnnotations) => {
            const sameLen = prevAnnotations.length === sorted.length;
            const same =
                sameLen && prevAnnotations.every((a, i) => a.__uid === sorted[i].__uid);

            if (!same && typeof onAnnotationsChange === 'function') {
                onAnnotationsChange(sorted);
            }
            return same ? prevAnnotations : sorted;
        });

        setAnnotationOrder((prevOrder) => {
            const sameLen = prevOrder.length === finalOrder.length;
            const same =
                sameLen && prevOrder.every((id, i) => id === finalOrder[i]);
            return same ? prevOrder : finalOrder;
        });

        setGroupAnnotationOrder((prevGroups) => {
            const next = Object.fromEntries(
                Object.entries(groupMap).map(([gid, keys]) => [gid, keys.slice()])
            );
            const sameKeys =
                Object.keys(prevGroups || {}).length === Object.keys(next).length &&
                Object.keys(prevGroups || {}).every((k) => k in next);
            let sameVals = sameKeys;
            if (sameVals) {
                for (const k of Object.keys(next)) {
                    const a = prevGroups[k] || [];
                    const b = next[k] || [];
                    if (a.length !== b.length || !a.every((v, i) => v === b[i])) {
                        sameVals = false;
                        break;
                    }
                }
            }
            return sameKeys && sameVals ? prevGroups : next;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [highlights, annotations, onAnnotationsChange]);


    const toggleAnnotation = (annotation) => {
        if (disableInteractions) return; // read-only
        const key = getAnnotationKey(annotation);

        if (multiSelectMode) {
            setSelectedKeys(prev =>
                prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
            );

            setMultiSelectTempKeys(prev =>
                prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
            );
        } else {
            setSelectedKeys(prev =>
                prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
            );
        }
    };


    const updateAnnotation = (key, updates) => {
        setAnnotations(prev =>
            prev.map((a) => {
                const currentKey = a.__uid || getAnnotationKey(a);
                if (currentKey !== key) return a;

                // Full replacement (used by draw->analyze to swap temp 'custom' into any type)
                if (updates && updates.__replaceWith) {
                    const repl = {...updates.__replaceWith};
                    // ensure stable identity/keys
                    const reprPage = Number(repl.page) || Number(a.page) || 1;
                    if (!repl.__uid) {
                        if (Array.isArray(repl.bbox)) {
                            repl.__uid = `${reprPage}_entity_${repl.bbox.join('_')}`;
                        } else if (repl.value?.bbox) {
                            repl.__uid = `${reprPage}_entity_${repl.value.bbox.join('_')}`;
                        } else if (repl.field?.bbox) {
                            repl.__uid = `${reprPage}_entity_${repl.field.bbox.join('_')}`;
                        } else {
                            repl.__uid = currentKey; // last resort
                        }
                    }
                    // Update selection to use the new key if bbox changed
                    if (a.__uid && currentKey === key) {
                        const oldUid = a.__uid;
                        const newUid = repl.__uid || getAnnotationKey(repl);

                        // If the bbox changed (uid is different), update selectedKeys
                        if (oldUid !== newUid) {
                            setSelectedKeys(prev =>
                                prev.map(k => k === oldUid ? newUid : k)
                            );
                        }
                    }
                    return repl;
                }

                const updated = {...a}; // Create a shallow copy of the annotation object

                if (updates.groupId !== undefined) {
                    updated.groupId = updates.groupId;
                }

                if (a.type === 'key_value') {
                    // Ensure new objects are created for field and value to maintain immutability
                    if (updates.field !== undefined) {
                        updated.field = {...updated.field, text: updates.field};
                    }
                    if (updates.value !== undefined) {
                        updated.value = {...updated.value, text: updates.value};
                    }
                } else if (a.type === 'entity') {
                    if (updates.entity_type !== undefined) {
                        updated.entity_type = updates.entity_type;
                    }
                    if (updates.text !== undefined) { // Change: Use 'text' here as per your ResizablePanel's updatePayload
                        updated.text = updates.text;
                    }
                } else if (a.type === 'summary_row') {
                    if (updates.label !== undefined) {
                        updated.label = updates.label;
                    }
                    if (updates.text !== undefined) {
                        updated.text = updates.text;
                    }
                } else if (a.type === 'table') {
                    if (updates.table_header && Array.isArray(updates.table_header)) {
                        updated.table_header = updates.table_header;
                    }
                    if (updates.rows && Array.isArray(updates.rows)) {
                        updated.rows = updates.rows;
                    }
                    if (updates.label !== undefined) {
                        updated.label = updates.label;
                    }
                }
                return updated;
            })
        );
    };


    const removeAnnotation = (annotation) => {
        const key = getAnnotationKey(annotation);

        // Add the key to the ref to prevent it from being re-added by the normalizer
        removedKeysRef.current.add(key);

        setAnnotations((prev) => prev.filter(a => getAnnotationKey(a) !== key)); // ⬅️ Remove it from list
        setSelectedKeys((prev) => prev.filter(k => k !== key)); // Unselect if selected
    };

    const unselectAnnotation = (annotation) => {
        const key = getAnnotationKey(annotation);
        setSelectedKeys((prev) => prev.filter(k => k !== key)); // Just unselect
    };

    const handleDissociateAnnotation = (annotation) => {
        const key = getAnnotationKey(annotation);
        updateAnnotation(key, {groupId: null});
    };

    const handleUpdateAnnotationPosition = async (key, annotation, boxType, newBbox, shouldReprocess = false) => {
        // Skip reprocessing if disabled (e.g., on results/history pages where parent handles backend update)
        const willReprocess = shouldReprocess && !disableReprocessOnDrag;

        // First, update the bbox immediately for visual feedback
        setAnnotations(prev =>
            prev.map((a) => {
                const currentKey = a.__uid || getAnnotationKey(a);
                if (currentKey !== key) return a;

                const updated = {...a};

                // Update bbox based on annotation type
                if (a.type === 'key_value') {
                    if (boxType === 'key_value_field' && a.field?.bbox) {
                        updated.field = {...updated.field, bbox: newBbox};
                    } else if (boxType === 'key_value_value' && a.value?.bbox) {
                        updated.value = {...updated.value, bbox: newBbox};
                    }
                } else if (Array.isArray(a.bbox)) {
                    // For entity, summary_row, table, etc.
                    updated.bbox = newBbox;
                }

                // If reprocessing, mark as loading
                if (willReprocess) {
                    updated.status = 'loading';
                    updated.text = 'Processing...';
                }

                return updated;
            })
        );

        // If reprocessing is disabled, just notify parent and return
        if (disableReprocessOnDrag && shouldReprocess) {
            if (typeof onAnnotationMoved === 'function') {
                // Capture page image and normalized bbox for text extraction
                let pageImage = null;
                let bboxNorm = null;
                if (newBbox && annotation?.page && pageRefs?.current) {
                    const pageIndex = Math.max(0, annotation.page - 1);
                    const pageNodeRef = pageRefs.current?.[pageIndex];
                    const pageEl = pageNodeRef?.current || pageNodeRef;
                    const canvas = pageEl?.querySelector?.('canvas.react-pdf__Page__canvas');
                    if (canvas && canvas.width && canvas.height) {
                        try {
                            pageImage = canvas.toDataURL('image/png');
                        } catch {}
                        const [x0pdf, y0pdf, x1pdf, y1pdf] = getBackendBboxFromDisplayBbox(newBbox);
                        const dpr = window.devicePixelRatio || 1;
                        const W = canvas.width, H = canvas.height;
                        const x0px = x0pdf * scale * dpr;
                        const y0px = y0pdf * scale * dpr;
                        const x1px = x1pdf * scale * dpr;
                        const y1px = y1pdf * scale * dpr;
                        const clamp01 = v => Math.max(0, Math.min(1, v));
                        bboxNorm = [
                            clamp01(x0px / W),
                            clamp01(y0px / H),
                            clamp01(x1px / W),
                            clamp01(y1px / H),
                        ];
                    }
                }

                onAnnotationMoved({
                    key,
                    annotation,
                    page: annotation.page,
                    bbox: newBbox,
                    oldAnnotation: annotation,
                    pageImage,
                    bboxNorm
                });
            }
            return;
        }

        // If we should reprocess, extract and analyze the new region
        if (willReprocess) {
            const pageIndex = annotation.page - 1;

            console.log('[handleUpdateAnnotationPosition] Reprocessing annotation:', {
                key,
                page: annotation.page,
                pageIndex,
                numPages,
                currentFileName: file?.name,
                annotationType: annotation.type
            });

            // Safety check: ensure the page exists in the current document
            if (pageIndex < 0 || pageIndex >= numPages) {
                console.error('❌ Page index out of range:', {pageIndex, numPages, annotationPage: annotation.page});
                updateAnnotation(key, {
                    status: 'error',
                    text: 'Page not found in current document'
                });
                return;
            }

            const pageElement = pageRefs.current[pageIndex];
            const canvas = pageElement?.querySelector('canvas.react-pdf__Page__canvas');

            if (!canvas) {
                console.error('❌ Canvas not found for reprocessing');
                updateAnnotation(key, {
                    status: 'error',
                    text: 'Canvas not found'
                });
                return;
            }

            // Additional safety check: verify canvas has content (width and height > 0)
            if (canvas.width === 0 || canvas.height === 0) {
                console.error('❌ Canvas has invalid dimensions:', {width: canvas.width, height: canvas.height});
                updateAnnotation(key, {
                    status: 'error',
                    text: 'Canvas not ready'
                });
                return;
            }

            // Match redraw: newBbox is the displayed box, while backend crop math uses
            // the same offset-corrected bbox that useDrawAnnotation emits as __raw_bbox.
            const apiBBox = getBackendBboxFromDisplayBbox(newBbox);

            const dpr = window.devicePixelRatio || 1;
            const [x0pdf, y0pdf, x1pdf, y1pdf] = apiBBox;
            const x0 = Math.round(x0pdf * scale * dpr);
            const y0 = Math.round(y0pdf * scale * dpr);
            const width = Math.max(0, Math.round((x1pdf - x0pdf) * scale * dpr));
            const height = Math.max(0, Math.round((y1pdf - y0pdf) * scale * dpr));

            if (width <= 0 || height <= 0) {
                console.error('❌ Invalid canvas dimensions:', {width, height});
                updateAnnotation(key, {
                    status: 'error',
                    text: 'Invalid dimensions'
                });
                return;
            }

            // Extract image from canvas
            const tempCanvas = document.createElement('canvas');
            tempCanvas.width = width;
            tempCanvas.height = height;
            const tempCtx = tempCanvas.getContext('2d');
            tempCtx.drawImage(canvas, x0, y0, width, height, 0, 0, width, height);
            const base64Image = tempCanvas.toDataURL('image/png');

            // Call backend to analyze the new region
            try {
                const {apiFetch} = await import('../../../api/api');
                const {API_BASE} = await import('../../../apiBase');

                const res = await apiFetch(`${API_BASE}/analyze-snippet`, {
                    method: 'POST',
                    headers: {'Content-Type': 'application/json'},
                    body: JSON.stringify({
                        image: base64Image,
                        page: annotation.page,
                        bbox: apiBBox,  // Use the API bbox without visual offsets
                        provider,
                        model,
                    }),
                });

                let data = {};
                try {
                    data = await res.json();
                } catch {
                    // non-JSON error body
                }

                if (!res.ok) {
                    const msg = data?.error || `HTTP ${res.status}`;
                    throw new Error(msg);
                }

                // Update annotation with new results
                const ttype = (data?.type || '').toLowerCase();
                let replacement = null;

                if (ttype === 'key_value') {
                    replacement = {
                        ...annotation,
                        type: 'key_value',
                        field: {text: String(data.field || '')},
                        value: {text: String(data.value || ''), bbox: newBbox},
                    };
                } else if (ttype === 'entity') {
                    replacement = {
                        ...annotation,
                        type: 'entity',
                        bbox: newBbox,
                        text: String(data.text || ''),
                        entity_type: String(data.entity_type || 'unknown'),
                    };
                } else if (ttype === 'summary_row') {
                    replacement = {
                        ...annotation,
                        type: 'summary_row',
                        bbox: newBbox,
                        label: String(data.label || 'Summary'),
                        text: String(data.text || ''),
                    };
                } else if (ttype === 'table') {
                    replacement = {
                        ...annotation,
                        type: 'table',
                        bbox: newBbox,
                        label: data.label || 'Table',
                        table_header: Array.isArray(data.table_header) ? data.table_header : [],
                        rows: Array.isArray(data.rows) ? data.rows : [],
                    };
                } else if (data?.key || data?.value) {
                    // Legacy fallback
                    replacement = {
                        ...annotation,
                        type: 'key_value',
                        field: {text: String(data.key || '')},
                        value: {text: String(data.value || ''), bbox: newBbox},
                    };
                }

                if (!replacement) {
                    throw new Error('Unrecognized snippet result');
                }

                updateAnnotation(key, {__replaceWith: replacement, status: 'done'});

                // Notify parent component that annotation was moved and reprocessed
                if (typeof onAnnotationMoved === 'function') {
                    onAnnotationMoved({
                        key,
                        annotation: replacement,
                        page: annotation.page,
                        bbox: newBbox,
                        oldAnnotation: annotation
                    });
                }
            } catch (err) {
                console.error('Error reprocessing annotation:', err);
                updateAnnotation(key, {
                    status: 'error',
                    text: 'Error: ' + err.message
                });
            }
        }
    };


// Add these two new handler functions inside your PdfViewer.js component

    const handleToggleDrawing = () => {
        if (disableInteractions) return;

        // Check the *future* state of isDrawing. If we are turning it ON...
        if (!isDrawing) {
            // ...then we must turn multi-select mode OFF.
            setMultiSelectMode(false);
            // Also perform the cleanup associated with cancelling group mode.
            setSelectedKeys(prev => prev.filter(k => !multiSelectTempKeys.includes(k)));
            setMultiSelectTempKeys([]);
            setGroupName('');
        }
        // Finally, toggle the drawing state.
        setIsDrawing(prev => !prev);
    };

    const handleToggleGroupMode = () => {
        if (disableInteractions) return;

        // Use the functional update to get the previous state
        setMultiSelectMode(prevMode => {
            const nextMode = !prevMode; // This is the new state for multiSelectMode

            if (nextMode) {
                // If we are turning group mode ON, turn drawing mode OFF.
                setIsDrawing(false);
                setMultiSelectTempKeys([]); // Reset temp keys for a new group selection
            } else {
                // If we are turning group mode OFF (cancelling), clean up selections.
                setSelectedKeys(prev => prev.filter(k => !multiSelectTempKeys.includes(k)));
                setMultiSelectTempKeys([]);
                setGroupName('');
            }
            return nextMode; // Return the new state for multiSelectMode
        });
    };

    const handleAddAnnotation = (newAnnotation) => {
        // Ensure consistent identity so jiggling can find it later
        const pageNum = Number(newAnnotation?.page) || 1;
        const structured = {
            ...newAnnotation,
            page: pageNum,
            type: newAnnotation?.type || 'entity',   // <- force a stable type
            groupId: newAnnotation?.groupId ?? defaultGroupIdForNewAnns ?? null,
        };
        // Match the table’s key scheme: `${page}_entity_${bbox.join('_')}`
        if (!structured.__uid && Array.isArray(structured.bbox) && structured.bbox.length === 4) {
            structured.__uid = `${pageNum}_entity_${structured.bbox.join('_')}`;
        } else if (!structured.__uid && structured.value?.bbox) {
            structured.__uid = `${pageNum}_entity_${structured.value.bbox.join('_')}`;
        } else if (!structured.__uid && structured.field?.bbox) {
            structured.__uid = `${pageNum}_entity_${structured.field.bbox.join('_')}`;
        }
        const key = getAnnotationKey(structured);

        setAnnotations((prev) => [...prev, structured]);
        setSelectedKeys((prev) => [...prev, key]);
        finishDrawing(); // leave draw mode after the first new box

        // 🔧 Tell parent a new rectangle was drawn (so it can override future peeks)
        try {
            if (typeof onAnnotationAdded === 'function') {
                // Normalize minimal shape the parent needs + page canvas dataUrl + normalized bbox
                let bbox = null;
                if (structured?.bbox) bbox = structured.bbox;
                else if (structured?.field?.bbox) bbox = structured.field.bbox;
                else if (structured?.value?.bbox) bbox = structured.value.bbox;

                let pageImage = null;
                let bboxNorm = null;
                if ((structured?.__raw_bbox || bbox) && structured?.page && pageRefs?.current) {
                    const pageIndex = Math.max(0, structured.page - 1);
                    const pageNodeRef = pageRefs.current?.[pageIndex];
                    const pageEl = pageNodeRef?.current || pageNodeRef;
                    const canvas = pageEl?.querySelector?.('canvas.react-pdf__Page__canvas');
                    if (canvas && canvas.width && canvas.height) {
                        // pageImage = entire page as data URL
                        try {
                            pageImage = canvas.toDataURL('image/png');
                        } catch {
                        }
                        // bboxNorm = coords normalized to the canvas pixel size
                        // ✅ normalize from RAW PDF bbox using scale × DPR to bitmap pixels, then / canvas bitmap size
                        const raw = structured.__raw_bbox || bbox;        // prefer raw
                        const [x0pdf, y0pdf, x1pdf, y1pdf] = raw;
                        const dpr = window.devicePixelRatio || 1;
                        const W = canvas.width, H = canvas.height;        // bitmap pixels
                        const x0px = x0pdf * scale * dpr;
                        const y0px = y0pdf * scale * dpr;
                        const x1px = x1pdf * scale * dpr;
                        const y1px = y1pdf * scale * dpr;
                        const clamp01 = v => Math.max(0, Math.min(1, v));
                        bboxNorm = [
                            clamp01(x0px / W),
                            clamp01(y0px / H),
                            clamp01(x1px / W),
                            clamp01(y1px / H),
                        ];
                    }
                }

                onAnnotationAdded({
                    page: structured.page,
                    bbox,
                    pageImage,
                    bboxNorm,
                    __raw_bbox: structured.__raw_bbox ?? bbox
                });
            }
        } catch {
        }

        setAnnotationOrder((prev) => {
            // wherever getTopY is defined in PdfViewer.js
            const getTopY = (a) => {
                if (Array.isArray(a?.bbox)) return a.bbox[1];
                if (a?.value?.bbox) return a.value.bbox[1];    // prefer value first
                if (a?.field?.bbox) return a.field.bbox[1];
                if (Array.isArray(a?.columns) && a.columns[0]?.bbox) return a.columns[0].bbox[1];
                return Infinity;
            };

            const allAnns = [...(annotations || []), structured].filter(Boolean);
            const keyToAnn = Object.fromEntries(allAnns.map(a => [getAnnotationKey(a), a]));

            const newEntry = {id: key, page: structured.page ?? 0, topY: getTopY(structured)};

            const mergedEntries = (prev || []).map((id) => {
                if (id.startsWith('group_')) {
                    const groupId = id.replace('group_', '');
                    const groupKeys = groupAnnotationOrder[groupId] || [];
                    const groupAnns = groupKeys.map(k => keyToAnn[k]).filter(Boolean);
                    if (groupAnns.length === 0) return {id, page: 0, topY: Infinity}; // ← guard
                    const topA = groupAnns.reduce((minA, a) => (getTopY(a) < getTopY(minA) ? a : minA), groupAnns[0]);
                    return {id, page: topA?.page ?? 0, topY: getTopY(topA)};
                } else {
                    const a = keyToAnn[id];
                    return a ? {id, page: a.page ?? 0, topY: getTopY(a)} : {id, page: 0, topY: Infinity}; // ← guard
                }
            });

            const final = [...mergedEntries, newEntry].sort((a, b) =>
                a.page !== b.page ? a.page - b.page : a.topY - b.topY
            );

            return final.map((e) => e.id);
        });


    };


    const lastExportRef = useRef(null);
    const localSelectedAnnotations = annotations.filter(a => {
        const key = a.__uid || getAnnotationKey(a);
        return selectedKeys.includes(key);
    });
    const finalSelectedAnnotations = forceAllSelected
        ? annotations
        : (selectedAnnotations ?? localSelectedAnnotations);
    useEffect(() => {
        if (typeof onExportData !== 'function') return;

// HighlightOverlay.js
        const getAnnotationKey = (a) => {
            if (a.__uid) return a.__uid;
            const base = `${a.page}_${a.type}`;

            if (a.type === 'key_value') {
                const v = a.value?.bbox?.join('_') || 'no_val_bbox';
                return `${base}_${v}`;
            }

            if (a.type === 'entity' || a.type === 'summary_row') return `${base}_${a.bbox?.join('_')}`;
            if (a.type === 'table_row' && Array.isArray(a.columns)) {
                const coords = a.columns.map(col => col.bbox?.join('_')).join('|');
                return `${base}_${coords}`;
            }
            if (a.type === 'table' && Array.isArray(a.bbox)) return `${base}_${a.bbox.join('_')}`;
            return `${base}_fallback_${Math.random().toString(36).slice(2, 8)}`;
        };


        const selectedKeySet = new Set(finalSelectedAnnotations.map(getAnnotationKey));
        const keyToAnnotation = new Map(annotations.map(a => [getAnnotationKey(a), a]));
        const result = [];

        for (const id of annotationOrder) {
            if (id.startsWith('group_')) {
                const groupId = id.replace('group_', '');
                const groupKeys = groupAnnotationOrder[groupId] || [];

                const groupMembers = groupKeys
                    .filter(key => selectedKeySet.has(key))
                    .map(key => keyToAnnotation.get(key))
                    .filter(Boolean);

                if (groupMembers.length > 0) {
                    result.push(...groupMembers);
                }
            } else if (selectedKeySet.has(id)) {
                const ann = keyToAnnotation.get(id);
                if (ann) result.push(ann);
            }
        }

        // Dedup
        const seen = new Set();
        const deduped = [];
        for (const ann of result) {
            const key = getAnnotationKey(ann);
            if (!seen.has(key)) {
                seen.add(key);
                deduped.push(ann);
            }
        }

        // Serialize deduped to JSON to compare
        const serialized = JSON.stringify(deduped);
        if (lastExportRef.current !== serialized) {
            lastExportRef.current = serialized;
            onExportData(deduped);
        }

    }, [selectedAnnotations, selectedKeys, annotationOrder, groupAnnotationOrder, annotations, onExportData]);

    const selectHook = useSelectAnnotationBox({
        pageRefs,
        scale,
        annotations,
        onSelect: (matched) => {
            const newKeys = matched.map(getAnnotationKey);

            // Always add to selectedKeys
            setSelectedKeys(prev => Array.from(new Set([...prev, ...newKeys])));

            // Add to multiSelectTempKeys only if in group mode
            if (multiSelectMode) {
                setMultiSelectTempKeys(prev => Array.from(new Set([...prev, ...newKeys])));
            }
        }
    });

    const handleCreateGroup = () => {
        if (!groupName.trim()) return;

        const rawGroupId = `${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
        const fullGroupId = `group_${rawGroupId}`;

        setGroupMeta(prev => ({
            ...prev,
            [rawGroupId]: {name: groupName}
        }));

        setAnnotations(prev =>
            prev.map((a) => {
                const key = getAnnotationKey(a);
                if (!multiSelectTempKeys.includes(key)) return a;
                if (a.groupId) return a;
                return {...a, groupId: rawGroupId};
            })
        );

        setGroupAnnotationOrder(prev => ({
            ...prev,
            [rawGroupId]: multiSelectTempKeys.slice(),
        }));

        setAnnotationOrder(prev => {
            const filtered = prev.filter(id => !multiSelectTempKeys.includes(id));
            const all = [...annotations];
            const keyToAnnotation = Object.fromEntries(all.map(a => [getAnnotationKey(a), a]));

            const getTopY = (a) => {
                if (a.bbox) return a.bbox[1];
                if (a.field?.bbox) return a.field.bbox[1];
                if (a.value?.bbox) return a.value.bbox[1];
                return Infinity;
            };

            const groupMembers = multiSelectTempKeys
                .map(k => keyToAnnotation[k])
                .filter(Boolean);

            // ✅ Guard: if nothing resolved, fall back to 0 so we don't crash
            if (groupMembers.length === 0) {
                const groupEntry = {id: fullGroupId, page: 0, topY: Infinity};
                const mergedEntries = filtered
                    .map(id => ({id, page: 0, topY: Infinity}))
                    .filter(Boolean);
                const sorted = [...mergedEntries, groupEntry].sort((a, b) =>
                    a.page !== b.page ? a.page - b.page : a.topY - b.topY
                );
                return sorted.map(e => e.id);
            }

            const topAnnotation = groupMembers.reduce((minA, a) => getTopY(a) < getTopY(minA) ? a : minA, groupMembers[0]);
            const topY = getTopY(topAnnotation);
            const page = topAnnotation.page || 0;

            const groupEntry = {
                id: fullGroupId,
                page,
                topY
            };

            const mergedEntries = [
                ...filtered.map(id => {
                    if (id.startsWith('group_')) {
                        const groupId = id.replace('group_', '');
                        const groupAnns = (groupAnnotationOrder[groupId] || [])
                            .map(k => keyToAnnotation[k])
                            .filter(Boolean);
                        if (groupAnns.length === 0) {
                            // ✅ Guard empty groups (can happen transiently)
                            return {id, page: 0, topY: Infinity};
                        }
                        const top = groupAnns.reduce(
                            (minA, a) => getTopY(a) < getTopY(minA) ? a : minA,
                            groupAnns[0]
                        );
                        return {id, page: top?.page || 0, topY: getTopY(top)};
                    } else {
                        const a = keyToAnnotation[id];
                        if (!a) return null; // ✅ Guard unknown ids
                        return {id, page: a.page || 0, topY: getTopY(a)};
                    }
                }),
                groupEntry
            ];

            const sorted = mergedEntries
                .filter(Boolean) // drop nulls
                .sort((a, b) => (a.page !== b.page ? a.page - b.page : a.topY - b.topY));
            return sorted.map(e => e.id);
        });

        setSelectedKeys(prev => Array.from(new Set([...prev, ...multiSelectTempKeys])));
        setMultiSelectTempKeys([]);
        setGroupName('');
        setMultiSelectMode(false);
    };

    useEffect(() => {
        if (multiSelectMode && multiSelectTempKeys.length > 1 && groupNameInputRef.current) {
            groupNameInputRef.current.focus();
        }
    }, [multiSelectMode, multiSelectTempKeys]);

    const isInitialMount = useRef(true);
    useEffect(() => {
        if (disableInteractions) return;
        if (typeof setSelectedAnnotations !== "function") return;


        // Guard against race conditions during mounting and state flickering.
        if (persistSelection) {
            // When persisting selection, we use a simple, robust guard:
            // Do not sync with the parent until the internal `annotations` list is hydrated.
            // This prevents wiping the parent state with an empty selection on remount.
            if (annotations.length === 0 && selectedAnnotations?.length > 0) {
                // console.log("%c[PdfViewer Sync Effect] Bailing out: waiting for annotations to hydrate.", "color: orange;");
                return;
            }
        } else {
            // Original guard logic for other parts of the app
            if (isInitialMount.current || (annotations.length === 0 && selectedAnnotations.length > 0)) {
                isInitialMount.current = false;
                return;
            }
        }

        if (isInitialMount.current) {
            isInitialMount.current = false;
        }

        const serializedNew = JSON.stringify(localSelectedAnnotations);
        const serializedPrev = JSON.stringify(selectedAnnotations);

        if (serializedNew !== serializedPrev) {
            setSelectedAnnotations(localSelectedAnnotations);
        }
    }, [selectedKeys, annotations, disableInteractions, setSelectedAnnotations, selectedAnnotations, persistSelection, localSelectedAnnotations]);

    return (
        <Box display="flex" height="100%" width="100%" position="relative">
            {/* LEFT PANEL */}
            <Box flexGrow={1} position="relative" display="flex" flexDirection="column">
                <Box sx={{flexGrow: 1, overflow: 'auto'}}>
                    <PdfScrollContainer
                        file={file}
                        viewerRef={viewerRef}
                        pageRefs={pageRefs}
                        onDocumentLoadSuccess={({numPages}) => setNumPages(numPages)}
                        scale={scale}
                        numPages={numPages}
                        highlights={annotations}
                        selectedAnnotations={finalSelectedAnnotations}
                        onBoxClick={disableInteractions ? noop : toggleAnnotation}
                        onZoomIn={handleZoomIn}
                        onZoomOut={handleZoomOut}
                        isDrawing={disableInteractions ? false : isDrawing}
                        onAddAnnotation={disableInteractions ? undefined : handleAddAnnotation}
                        onFinishDrawing={disableInteractions ? undefined : finishDrawing}
                        updateAnnotation={disableInteractions ? undefined : updateAnnotation}
                        provider={provider}
                        model={model}
                        onDissociateAnnotation={disableInteractions ? undefined : handleDissociateAnnotation}
                        onRemoveAnnotation={disableInteractions ? undefined : removeAnnotation}
                        onUpdateAnnotationPosition={disableInteractions ? undefined : handleUpdateAnnotationPosition}
                        selectionRect={disableInteractions ? undefined : selectHook.currentRect}
                        selectionPage={disableInteractions ? undefined : selectHook.currentPage}
                        selectionHandlers={disableInteractions ? undefined : {
                            handleMouseDown: selectHook.handleMouseDown,
                            handleMouseMove: selectHook.handleMouseMove,
                            handleMouseUp: selectHook.handleMouseUp,
                        }}
                        groupingTempKeys={multiSelectTempKeys}
                        multiSelectMode={multiSelectMode}
                        peekKey={peekKey}
                        hoveredKey={hoveredKey}
                        onBoxHover={(key) => {
                            setHoveredKey(key);
                            onBoxHover?.(key);
                        }}
                        suppressProcessing={redrawOnly}
                    />
                </Box>

                {/* Execution time */}
                {executionTime && (
                    <Box
                        boxShadow={3}
                        position="absolute"
                        bottom={8}
                        right={30}
                        bgcolor={alpha(theme.palette.background.default, 0.8)}
                        color={theme.palette.text.primary}
                        px={2}
                        py={0.5}
                        borderRadius={1}
                        fontSize="0.75rem"
                        zIndex={1500}
                        pointerEvents="none"
                    >
                        ⏱️ {executionTime}s
                    </Box>
                )}

            </Box>

            {/* Zoom + Draw controls in a draggable toolbar*/}
            {file && !hideToolbar && (
                <Draggable
                    nodeRef={dragRef}
                    handle=".drag-handle"
                    bounds="parent"
                    onStart={() => setIsDragging(true)}
                    onStop={() => setIsDragging(false)}
                >
                    <Box
                        ref={dragRef}
                        position="absolute"
                        top={16}
                        left={16}
                        zIndex={1200}
                        display="flex"
                        alignItems="center"
                        bgcolor={theme.palette.background.default}
                        p={1}
                        borderRadius={1}
                        boxShadow={2}
                    >
                        {/* Grip handle on the left */}
                        <Box
                            className="drag-handle"
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mr: 1,
                                borderRadius: '4px',
                                cursor: 'grab',
                                userSelect: 'none',
                                transition: 'background-color 0.2s ease',
                                backgroundColor: isDragging
                                    ? alpha(theme.palette.secondary.main, 0.3) // mint with 30% opacity
                                    : 'transparent',
                            }}
                            title="Move toolbar"
                        >
                            <DragIndicatorIcon
                                fontSize="small"
                                color="secondary.main"
                            />
                        </Box>
                        {showZoomControls && (
                            <ZoomControls onZoomIn={handleZoomIn} onZoomOut={handleZoomOut}/>
                        )}
                        {showAnnotateControls && (
                            <>
                                <Button
                                    onClick={handleToggleDrawing}
                                    disabled={disableInteractions}
                                    color={isDrawing ? 'primary' : 'default'}
                                    variant="text"
                                    startIcon={<CreateIcon/>}
                                    sx={{
                                        textTransform: 'none', fontSize: '0.875rem', borderRadius: '8px',
                                        transition: 'box-shadow 0.2s ease-in-out',
                                        '&:hover': {
                                            backgroundColor: alpha(theme.palette.info.main, 0.3),
                                            boxShadow: theme.shadows[2]
                                        }
                                    }}
                                >
                                    {t("new_annotation")}
                                </Button>
                                <Button
                                    onClick={handleToggleGroupMode}
                                    disabled={disableInteractions}
                                    color={multiSelectMode ? 'error' : 'default'}
                                    variant="text"
                                    startIcon={<GroupWorkIcon/>}
                                    sx={{
                                        textTransform: 'none', fontSize: '0.875rem', borderRadius: '8px',
                                        transition: 'box-shadow 0.2s ease-in-out',
                                        '&:hover': {
                                            backgroundColor: alpha(theme.palette.info.main, 0.3),
                                            boxShadow: theme.shadows[2]
                                        }
                                    }}
                                >
                                    {t("new_group")}
                                </Button>
                            </>
                        )}
                    </Box>
                </Draggable>
            )}

            {/* Page indicator */}
            {file && numPages && (
                <Box position="absolute" bottom={16} left={16} zIndex={1200}>
                    <PageIndicator currentPage={currentPage} numPages={numPages}/>
                </Box>
            )}

            {/* Loading overlay */}
            {loading && (
                <Box
                    position="absolute"
                    top={0}
                    left={0}
                    width="100%"
                    height="100%"
                    bgcolor='transparent'
                    zIndex={1000}
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                >
                    <CircularProgress size={60}/>
                    <Typography mt={2} variant="body1" color={theme.palette.text.primary_dark}>
                        {t("analyzing_file")}
                    </Typography>
                </Box>
            )}

            {/* RIGHT PANEL */}
            {!hideSelectionPanel && (
                <ResizablePanel
                    selectedAnnotations={finalSelectedAnnotations}
                    onUpdateAnnotation={(key, updates) => updateAnnotation(key, updates)}
                    onRemoveAnnotation={unselectAnnotation}
                    setAnnotations={setAnnotations}
                    annotationOrder={annotationOrder}
                    setAnnotationOrder={setAnnotationOrder}
                    groupAnnotationOrder={groupAnnotationOrder}
                    setGroupAnnotationOrder={setGroupAnnotationOrder}
                    groupMeta={groupMeta}
                    setGroupMeta={setGroupMeta}
                    onPeek={requestPeek}
                    onHover={setHoveredKey}
                    hoveredKey={hoveredKey}
                    tableWarningText={tableWarningText}
                />
            )}
            {multiSelectMode && multiSelectTempKeys.length > 1 && (
                <Box
                    position="absolute"
                    bottom={80}
                    left={16}
                    zIndex={1200}
                    bgcolor={theme.palette.background.default}
                    p={2}
                    borderRadius={2}
                    boxShadow={2}
                >
                    <TextField
                        inputRef={groupNameInputRef}
                        label={t("group_name")}
                        value={groupName}
                        onChange={(e) => setGroupName(e.target.value)}
                        size="small"
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                handleCreateGroup();
                            }
                        }}
                        sx={{
                            mr: 1,
                            '& .MuiOutlinedInput-root': {
                                '& fieldset': {
                                    borderColor: theme.palette.error.main,
                                },
                                '&:hover fieldset': {
                                    borderColor: theme.palette.error.main,
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: theme.palette.error.main,
                                },
                            },
                            '& .MuiInputLabel-root': {
                                color: theme.palette.error.main,
                            },
                            '& .MuiInputLabel-root.Mui-focused': {
                                color: theme.palette.error.main,
                            },
                        }}
                    />

                    <Button
                        variant="contained"
                        disabled={!groupName.trim()}
                        sx={{
                            backgroundColor: theme.palette.error.main,
                            color: 'default',
                            '&:hover': {
                                backgroundColor: theme.palette.error.main,
                            },
                        }}
                        onClick={handleCreateGroup}
                    >
                        {t("create_group")}
                    </Button>
                    <Button
                        variant="outlined"
                        sx={{
                            ml: 1,
                            borderColor: theme.palette.primary.main,
                            '&:hover': {borderColor: theme.palette.secondary.main}
                        }}
                        onClick={() => {
                            if (multiSelectMode) {
                                // Cancel multi-select mode and clear related selections
                                setSelectedKeys(prev =>
                                    prev.filter(k => !multiSelectTempKeys.includes(k))
                                );
                                setMultiSelectTempKeys([]);
                                setGroupName('');
                                setMultiSelectMode(false);
                            } else {
                                setMultiSelectMode(true);
                                setMultiSelectTempKeys([]);
                            }
                        }}
                    >
                        {t("cancel")}
                    </Button>
                </Box>
            )}
        </Box>
    );
}

export default PdfViewer;
